import * as React from 'react';
import * as Api from '@ViewModels';
import type { Edge, Node } from '@xyflow/react';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import type { BaseNodeData } from '../types';
import { convertTemplateToFlow } from '../utils/convertTemplateToFlow';

/**
 * Parameters for the useAutomationFlow hook.
 */
interface UseAutomationFlowParams {
	/** The automation template to expand into a flow */
	template: Api.IAutomationTemplateReference | null;
	/** Whether to use the draft version (true) or published version (false) */
	useDraft: boolean;
	/** Optional impersonation context for admin operations */
	impersonationContext?: Api.IImpersonationContext;
	/**
	 * Cache buster token used to force re-expansion.
	 * This is required when child/branch templates change because the root
	 * template's signature may not change.
	 */
	cacheBuster?: number;
}

/**
 * Return value from the useAutomationFlow hook.
 */
interface UseAutomationFlowResult {
	/** Nodes for the React Flow graph */
	nodes: Node<BaseNodeData>[];
	/** Edges for the React Flow graph */
	edges: Edge[];
	/** Whether the flow is currently being expanded */
	isExpanding: boolean;
	/** Which version was last expanded, null if no expansion yet */
	expandedVersion: 'draft' | 'published' | null;
}

/**
 * Cache entry for storing expanded flow results.
 */
interface CacheEntry {
	nodes: Node<BaseNodeData>[];
	edges: Edge[];
	templateUpdatedOn?: string;
}

function buildCaseSignature(step: Api.IAutomationStep): string {
	const caseStatements = (
		(step as { cases?: { id?: string; name?: string; _type?: string; caseType?: string; isDefault?: boolean }[] })
			.cases ?? []
	)
		.map(caseStatement => {
			const behaviorCase = caseStatement as {
				id?: string;
				name?: string;
				_type?: string;
				caseType?: string;
				isDefault?: boolean;
				engagementType?: string | number;
				automationTemplateId?: string;
			};
			return [
				behaviorCase.id || 'no-id',
				behaviorCase._type || behaviorCase.caseType || 'no-type',
				behaviorCase.name || '',
				behaviorCase.isDefault ? 'default' : 'case',
				String(behaviorCase.engagementType || ''),
				behaviorCase.automationTemplateId || '',
			].join(':');
		})
		.join(',');

	return caseStatements;
}

function buildStepSignature(step: Api.IAutomationStep): string {
	const stepDetails = step as {
		sourceStepId?: string;
		waitTimeout?: string;
		schedule?: Api.IAutomationStepSchedule;
		name?: string;
	};

	return [
		step.id,
		step._type,
		stepDetails.name || '',
		stepDetails.sourceStepId || '',
		stepDetails.waitTimeout || '',
		JSON.stringify(stepDetails.schedule || null),
		buildCaseSignature(step),
	].join('|');
}

/**
 * Expands an automation template into a React Flow graph structure.
 *
 * This hook converts an automation template (trigger + steps) into nodes and edges
 * suitable for rendering with React Flow. It handles:
 * - Draft vs published version selection
 * - Recursive expansion of switch node branches
 * - Caching of expanded results to avoid redundant computation
 * - Memory management by limiting cache size
 *
 * Note: This hook no longer enriches nodes with isReadOnly. The readonly state
 * is now determined by the canvas mode and applied at the canvas level via
 * useCanvasMode hook.
 *
 * @example
 * ```typescript
 * const { useDraft } = useDraftVersionState(template);
 * const { nodes, edges, isExpanding } = useAutomationFlow({
 *   template,
 *   useDraft,
 * });
 *
 * if (isExpanding) {
 *   return <LoadingSpinner />;
 * }
 *
 * return (
 *   <AutomationFlowCanvas mode="editing" initialNodes={nodes} initialEdges={edges} />
 * );
 * ```
 */
export function useAutomationFlow({
	template,
	useDraft,
	impersonationContext,
	cacheBuster,
}: UseAutomationFlowParams): UseAutomationFlowResult {
	const userSession = useUserSession();
	const [nodes, setNodes] = React.useState<Node<BaseNodeData>[]>([]);
	const [edges, setEdges] = React.useState<Edge[]>([]);
	const [isExpanding, setIsExpanding] = React.useState(false);
	const [expandedVersion, setExpandedVersion] = React.useState<'draft' | 'published' | null>(null);

	// Enhanced cache that tracks version timestamps
	const flowCacheRef = React.useRef<Map<string, CacheEntry>>(new Map());
	// Track the last root template reference used to build the flow. This lets us
	// bypass the cache when the parent query intentionally publishes a fresh template
	// object for branch-only updates that do not change the root step signature.
	const lastExpandedTemplateRef = React.useRef<Api.IAutomationTemplateReference | null>(null);

	React.useEffect(() => {
		if (!template || !userSession) {
			lastExpandedTemplateRef.current = null;
			setNodes([]);
			setEdges([]);
			setIsExpanding(false);
			return;
		}

		const templateId = template.id;
		// IAutomationTemplateReference can contain draft/published versions
		const templateWithVersions = template as unknown as Api.IAutomationTemplate;

		// Determine which version to use.
		// - Published view must only ever show the published root version.
		// - Draft view prefers the draft root version, but falls back to the published
		//   root version so descendant-only drafts can still render correctly.
		const resolvedVersion = useDraft
			? (templateWithVersions.draft ?? templateWithVersions.published)
			: templateWithVersions.published;

		// Handle the "no version" case:
		// - If the template has NO versions at all (brand-new manual automation where
		//   manual triggers are null triggers), synthesize an empty version so the
		//   Manual trigger placeholder renders and the user can start adding steps.
		// - If the template has the OTHER version but not the requested one
		//   (e.g. viewing the published tab on a draft-only template), do NOT
		//   synthesize — showing a lone "Manual" trigger there is misleading. Bail
		//   out with an empty canvas and let the preview toggle handle it.
		let version: { steps?: Api.IAutomationStep[]; trigger?: Api.IAutomationTrigger } | undefined = resolvedVersion;
		if (!version) {
			const hasAnyVersion = Boolean(templateWithVersions.draft || templateWithVersions.published);
			if (hasAnyVersion) {
				lastExpandedTemplateRef.current = null;
				setNodes([]);
				setEdges([]);
				setIsExpanding(false);
				setExpandedVersion(useDraft ? 'draft' : 'published');
				return;
			}
			version = { steps: [], trigger: undefined };
		}

		// Generate cache key based on template ID, version, and last-modified timestamp.
		// Fall back to a lightweight step signature so case changes invalidate the cache.
		const templateLastModified = (template as { lastModifiedDate?: string }).lastModifiedDate;
		const templateUpdatedOn = (template as { updatedOn?: string }).updatedOn;
		const versionSignature = [
			version.trigger?._type ?? 'no-trigger',
			...(version.steps ?? []).map(step => buildStepSignature(step)),
		].join('||');
		const lastModifiedToken = templateLastModified || templateUpdatedOn || 'no-last-modified';
		const cacheBusterToken = cacheBuster ?? 0;
		const cacheKey = `${templateId}-${useDraft ? 'draft' : 'published'}-${lastModifiedToken}-${versionSignature}-${cacheBusterToken}`;

		// Check cache first. Reuse it only when the exact same root template reference
		// is driving the expansion. Nested branch edits can re-publish a fresh root
		// template object without changing the root step signature; in that case we must
		// re-expand so branch templates are fetched again instead of serving stale nodes.
		const cachedEntry = flowCacheRef.current.get(cacheKey);
		const canReuseCachedExpansion = cachedEntry && lastExpandedTemplateRef.current === template;
		if (canReuseCachedExpansion) {
			// Only update state if values actually changed to prevent infinite render loops
			setNodes(prev => (prev === cachedEntry.nodes ? prev : cachedEntry.nodes));
			setEdges(prev => (prev === cachedEntry.edges ? prev : cachedEntry.edges));
			setIsExpanding(false);
			setExpandedVersion(useDraft ? 'draft' : 'published');
			return;
		}

		// Expand flow using the shared converter
		const expandFlow = async () => {
			setIsExpanding(true);
			try {
				// Create a proper IAutomationTemplate structure for the converter
				// Include all template properties needed for trigger settings (canEdit, sendToHousehold, etc.)
				const fullTemplate = template as unknown as Api.IAutomationTemplate & { canEdit?: boolean };
				const templateForConversion: Api.IAutomationTemplate & { canEdit?: boolean } = {
					id: template.id,
					name: template.name,
					draft: useDraft ? version : undefined,
					published: !useDraft ? version : undefined,
					status: template.status,
					templateType: Api.TemplateType.Automation,
					scope: fullTemplate.scope,
					// Include properties needed for trigger settings parity with list view
					// Default canEdit to true since we're in edit mode (undefined would disable checkboxes)
					canEdit: fullTemplate.canEdit ?? true,
					sendToHousehold: fullTemplate.sendToHousehold,
					cancelOnReply: fullTemplate.cancelOnReply,
					allowUnsubscribed: fullTemplate.allowUnsubscribed,
					contactFilter: fullTemplate.contactFilter,
				};

				// Use the shared converter with branch expansion
				const result = await convertTemplateToFlow(templateForConversion, {
					userSession,
					impersonationContext,
					expandBranches: true,
				});

				// Cache the result
				const cacheEntry: CacheEntry = {
					nodes: result.nodes,
					edges: result.edges,
					templateUpdatedOn,
				};
				flowCacheRef.current.set(cacheKey, cacheEntry);

				// Clear old cache entries to prevent memory leaks (keep last 10)
				if (flowCacheRef.current.size > 10) {
					const entries = Array.from(flowCacheRef.current.entries());
					const toDelete = entries.slice(0, entries.length - 10);
					toDelete.forEach(([key]) => flowCacheRef.current.delete(key));
				}

				lastExpandedTemplateRef.current = template;
				setNodes(result.nodes);
				setEdges(result.edges);
				setExpandedVersion(useDraft ? 'draft' : 'published');
			} catch (err) {
				// IMPORTANT: do not silently swallow expansion errors.
				// A blank canvas with no nodes is extremely hard to debug without this.
				console.error('[useAutomationFlow] Failed to expand automation flow', {
					templateId: template.id,
					useDraft,
					error: err,
				});
				setNodes([]);
				setEdges([]);
			} finally {
				setIsExpanding(false);
			}
		};

		expandFlow();
	}, [template, useDraft, impersonationContext, userSession, cacheBuster]);

	return { nodes, edges, isExpanding, expandedVersion };
}
